import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchFrequentlyBoughtTogetherProducts } from "~/features/productDetails/components/FrequentlyBoughtTogetherSection/fetchFrequentlyBoughtTogetherProducts";
import type { ProductData } from "~/domains/product/types/ProductData";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { usePriceReferenceSelection } from "~/app/config/useSession";

type FrequentlyBoughtTogetherProductsState = "loading" | "loaded" | "error";

export interface FrequentlyBoughtTogetherData {
  loadingFBT: boolean;
  showFBTComponent: boolean;
  mainProduct?: ProductDescriptor;
  firstProduct?: ProductDescriptor;
  secondProduct?: ProductDescriptor;
}

type FrequentlyBoughtTogetherProducts = Awaited<ReturnType<typeof fetchFrequentlyBoughtTogetherProducts>>;

export const useFrequentlyBoughtTogetherProducts = (
  productCode: string,
  featureEnabled: boolean,
): FrequentlyBoughtTogetherData => {
  const [state, setState] = useState<FrequentlyBoughtTogetherProductsState>("loading");
  const [products, setProducts] = useState<FrequentlyBoughtTogetherProducts | null>(null);
  const dispatch = useDispatch();
  const { selection } = usePriceReferenceSelection();

  useEffect(() => {
    (async () => {
      if (!featureEnabled) {
        return;
      }
      const products = await fetchFrequentlyBoughtTogetherProducts(productCode);
      dispatch(addBaseProductDataAction([...products.first, ...products.second]));

      setProducts(products);
      setState("loaded");
    })();
  }, [productCode, featureEnabled]);

  const productCodes = products ? [...products.first, ...products.second].map((product) => product.code) : [];

  const mainProduct = ProductService.useProductDescriptor({
    productCode: productCode,
    priceReference: getInitialPriceReference(selection),
    options: { fetch: true },
  });

  const productDescriptors = ProductService.useMultipleProductDescriptors({
    productCodes: productCodes,
    priceReference: getInitialPriceReference(selection),
    options: { fetch: true },
  });

  if (!featureEnabled) {
    return {
      loadingFBT: false,
      showFBTComponent: false,
    };
  }

  if (state === "loading") {
    return {
      loadingFBT: true,
      showFBTComponent: true,
    };
  }

  let firstProduct: ProductDescriptor | undefined = undefined;
  let secondProduct: ProductDescriptor | undefined = undefined;

  function pickProduct(
    firstRelation: ProductData[] | undefined,
    secondRelation: ProductData[] | undefined,
    excludeProduct?: ProductDescriptor,
  ) {
    const productsToCheck = [...(firstRelation ?? []), ...(secondRelation ?? [])]
      .map((product) => productDescriptors[product.code])
      .filter((product) => product !== excludeProduct);
    if (!userHasPermission(Permission.AVAILABILITY)) {
      return productsToCheck[0];
    } else {
      for (const productDescriptor of productsToCheck) {
        if (!excludeProduct || excludeProduct !== productDescriptor) {
          return productDescriptor;
        }
      }
      return undefined;
    }
  }
  firstProduct = pickProduct(products?.first, products?.second);
  if (firstProduct) {
    secondProduct = pickProduct(products?.second, products?.first, firstProduct);
  }

  const allSecondProductCandidates = [...(products?.first ?? []), ...(products?.second ?? [])].filter(
    (product) => product.code !== firstProduct?.base.code,
  );

  return {
    // False when there's a second product. Otherwise true if not all loaded
    loadingFBT: !secondProduct && allSecondProductCandidates.length > 0,
    // Show when there's a first product or we're still loading
    showFBTComponent: !!firstProduct,
    mainProduct,
    firstProduct,
    secondProduct,
  };
};
